/**
 * RoadmapComment Remapper
 * Flutter source: lib/data/models/roadmap_comment.dart (+ .g.dart)
 *
 * Bridges Task 1's RoadmapCommentDto to the domain RoadmapCommentEntity.
 * Mirrors the content CommentRemapper sibling, but with roadmap fields
 * (roadmapItemId/roadmapUserId, no mediaInfo).
 */

import type { RoadmapCommentDto } from '@/data/dtos/roadmapComment/roadmapCommentDto';
import type { JsonObject } from '@/data/types/json';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';
import { RoadmapCommentReplyRemapper } from '@/domain/remappers/roadmapComment/roadmapCommentReplyRemapper';

export class RoadmapCommentRemapper {
  static fromJson(data: JsonObject): RoadmapCommentDto {
    const commentToReply = data['commentToReply'] as JsonObject | undefined;
    return {
      id: (data['id'] as string | undefined) || '',
      parentId: (data['parentId'] as string | null | undefined) ?? null,
      text: (data['text'] as string | undefined) || '',
      commentToReply: commentToReply
        ? RoadmapCommentReplyRemapper.fromJson(commentToReply)
        : null,
      replies: (data['replies'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      popularity: (data['popularity'] as number | undefined) ?? 0,
      createdTimestamp: (data['createdTimestamp'] as number | undefined) ?? 0,
      roadmapItemId: (data['roadmapItemId'] as string | undefined) || '',
      userId: (data['userId'] as string | undefined) || '',
      // Flutter fromJson: json['roadmapUserId'] as String? ?? ""
      roadmapUserId: (data['roadmapUserId'] as string | undefined) || '',
      userName: (data['userName'] as string | undefined) || '',
      // Flutter fromJson: json['username'] as String? ?? ""
      username: (data['username'] as string | undefined) || '',
    };
  }

  static toEntity(data: JsonObject): RoadmapCommentEntity {
    const dto = RoadmapCommentRemapper.fromJson(data);
    return {
      id: dto.id,
      parentId: dto.parentId ?? null,
      text: dto.text,
      commentToReply: dto.commentToReply
        ? RoadmapCommentReplyRemapper.toEntity(dto.commentToReply)
        : null,
      replies: dto.replies,
      likes: dto.likes,
      popularity: dto.popularity,
      createdTimestamp: dto.createdTimestamp,
      roadmapItemId: dto.roadmapItemId,
      userId: dto.userId,
      roadmapUserId: dto.roadmapUserId,
      userName: dto.userName,
      username: dto.username,
      parent: null,
      children: [],
      lastFetchedChild: null,
      hasMoreChildren: true,
      childrenFetched: 0,
      isLastChild: false,
      isFetchingChildren: false,
      isInitialComment: false,
    };
  }

  static toDto(entity: RoadmapCommentEntity): RoadmapCommentDto {
    return {
      id: entity.id,
      parentId: entity.parentId ?? null,
      text: entity.text,
      commentToReply: entity.commentToReply
        ? RoadmapCommentReplyRemapper.toDto(entity.commentToReply)
        : null,
      replies: entity.replies,
      likes: entity.likes,
      popularity: entity.popularity,
      createdTimestamp: entity.createdTimestamp,
      roadmapItemId: entity.roadmapItemId,
      userId: entity.userId,
      roadmapUserId: entity.roadmapUserId,
      userName: entity.userName,
      username: entity.username,
    };
  }

  /**
   * Plain JSON object for backend writes. Emits exactly the 13 serialized keys
   * Flutter's _$RoadmapCommentToJson produces (no transient fields);
   * parentId/commentToReply serialize as null when absent.
   */
  static toJson(dto: RoadmapCommentDto): JsonObject {
    return {
      id: dto.id,
      parentId: dto.parentId ?? null,
      text: dto.text,
      commentToReply: dto.commentToReply
        ? RoadmapCommentReplyRemapper.toJson(dto.commentToReply)
        : null,
      replies: dto.replies,
      likes: dto.likes,
      popularity: dto.popularity,
      createdTimestamp: dto.createdTimestamp,
      roadmapItemId: dto.roadmapItemId,
      userId: dto.userId,
      roadmapUserId: dto.roadmapUserId,
      userName: dto.userName,
      username: dto.username,
    };
  }
}
